import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Card, Button, Table, Spinner, Form, Nav } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { BIEUMAUTUYENSINH_API, BaseGatewayImage } from '../../../../../utils/apiUrl';
import { loaiTuyenSinhValue, objectLoaiTuyenSinh, objectShowNumber, role } from '../../../../../utils/define';
import { Get, DeleteSingle } from '../../../../../utils/httpProvider';
import { createUrlWithParam, checkApiPermission } from '../../../../../utils/helper';
import { toast } from 'react-toastify';
import Pagination from 'react-js-pagination';
import BieuMauTuyenSinhAdd from './BieuMauTuyenSinhAdd';
import BieuMauTuyenSinhEdit from './BieuMauTuyenSinhEdit';
import { ConfirmModal } from '../../../../../modules/modal/ConfirmModal';
import { userManager } from '../../../../../utils/oidc';

const BieuMauTuyenSinh = () => {
    const queryClient = useQueryClient();
    const history = useHistory();
    const location = useLocation();
    const mappings = useSelector((state) => state.apiRoleMapping?.listApiRoleMapping || []);

    const [showModalAdd, setShowModalAdd] = useState(false);
    const [showModalEdit, setShowModalEdit] = useState(false);
    const [dataEdit, setDataEdit] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [canAdd, setCanAdd] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [canDelete, setCanDelete] = useState(false);
    
    // Pagination states
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [keyword, setKeyword] = useState('');
    const [searchKeyword, setSearchKeyword] = useState('');
    const [activeTab, setActiveTab] = useState(loaiTuyenSinhValue.tuyenSinhTienSi);

    useEffect(() => {
        const paramUrl = new URLSearchParams(location.search.substring(1));
        if (paramUrl.get('page') != null) {
            const parsedPage = parseInt(paramUrl.get('page'), 10);
            if (!isNaN(parsedPage)) setPage(parsedPage);
        }
        if (paramUrl.get('size') != null) {
            const parsedSize = parseInt(paramUrl.get('size'), 10);
            if (!isNaN(parsedSize)) setPageSize(parsedSize);
        }
        if (paramUrl.get('search') != null) {
            setSearchKeyword(paramUrl.get('search'));
            setKeyword(paramUrl.get('search'));
        }
        if (paramUrl.get('tab') != null && paramUrl.get('tab') !== 'NaN') {
            setActiveTab(paramUrl.get('tab'));
        }

        const fetchRole = async () => {
            const user = await userManager.getUser();
            if (user && user.profile && user.profile.role) {
                const userRoles = Array.isArray(user.profile.role) ? user.profile.role : [user.profile.role];
                setCanAdd(checkApiPermission(mappings, userRoles, 'BieuMauTuyenSinh', 'Create'));
                setCanEdit(checkApiPermission(mappings, userRoles, 'BieuMauTuyenSinh', 'UpdateItem'));
                setCanDelete(checkApiPermission(mappings, userRoles, 'BieuMauTuyenSinh', 'DeleteItem'));
            }
        };
        fetchRole();
    }, [mappings]);

    const replaceHistory = (Page, Size, Search, Tab) => {
        history.replace(
            createUrlWithParam(location.pathname, {
                page: Page,
                size: Size,
                search: Search,
                tab: Tab
            })
        );
    };

    // Delete states
    const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
    const [idWantDelete, setIdWantDelete] = useState('');
    const [confirmDelMessage, setConfirmDelMessage] = useState('');

    const { isLoading, data: queryData, refetch } = useQuery(
        ['bieuMauTuyenSinhData', page, pageSize, searchKeyword, activeTab],
        () => {
            const model = {
                Keyword: searchKeyword,
                PageIndex: page,
                PageSize: pageSize,
                LoaiTuyenSinh: activeTab
            };
            replaceHistory(page, pageSize, searchKeyword, activeTab);
            return Get(BIEUMAUTUYENSINH_API.GET_PAGING, null, model);
        },
        { 
            keepPreviousData: true 
        }
    );

    const items = queryData?.data?.$values ? queryData.data.$values : queryData?.data || [];
    const totalRecords = queryData?.totalRecords || 0;

    useEffect(() => {
        if (items && items.length > 0) {
            setSelectedRow(prev => {
                if (prev) {
                    const fresh = items.find(item => item.id === prev.id);
                    return fresh || items[0];
                }
                return items[0];
            });
        } else {
            setSelectedRow(null);
        }
    }, [queryData]);

    const changePage = (number) => {
        setPage(number);
        replaceHistory(number, pageSize, searchKeyword, activeTab);
    };

    const onChangeShowNumber = (e) => {
        const newSize = Number(e.target.value);
        setPageSize(newSize);
        setPage(1);
        replaceHistory(1, newSize, searchKeyword, activeTab);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        setSearchKeyword(keyword);
        replaceHistory(1, pageSize, keyword, activeTab);
    };

    const deleteFunc = useMutation((id) => DeleteSingle(BIEUMAUTUYENSINH_API.DELETE_ITEM, id, null));

    const handleAdd = () => {
        setShowModalAdd(true);
    };

    const handleEdit = (item) => {
        setDataEdit(item);
        setShowModalEdit(true);
    };

    const deleteAction = (id, name) => {
        setIdWantDelete(id);
        setConfirmDelMessage(name || 'Bản ghi này');
        setOpenConfirmDeleteModal(true);
    };

    const acceptDelete = () => {
        if (!idWantDelete) return;

        deleteFunc.mutate(idWantDelete, {
            onSuccess: (res) => {
                if (res && (res.status === 200 || res.status === 201 || res.status === 204)) {
                    toast.success('Xóa thành công');
                    queryClient.invalidateQueries(['bieuMauTuyenSinhData']);
                } else {
                    toast.error(res?.message || 'Có lỗi xảy ra khi xóa');
                }
            },
            onError: (err) => {
                toast.error(err?.message || 'Có lỗi xảy ra');
            }
        });

        setOpenConfirmDeleteModal(false);
        setIdWantDelete('');
    };

    const cancelDelete = () => {
        setIdWantDelete('');
        setOpenConfirmDeleteModal(false);
    };

    const getLabelFromEnum = (value, objectEnum, valueMap) => {
        if (value === null || value === undefined) return '';
        const actualValue = typeof value === 'string' && valueMap ? valueMap[value] : value;
        const found = objectEnum.find((item) => item.value === actualValue);
        return found ? found.label : '';
    };

    return (
        <div className="container-fluid">
            <Card className="card-sticky-layout card-sticky-layout-lg">
                <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                        <Nav variant="pills" className="flex-row">
                            <Nav.Item>
                                <Nav.Link
                                    active={activeTab === loaiTuyenSinhValue.tuyenSinhTienSi}
                                    onClick={() => { 
                                        setActiveTab(loaiTuyenSinhValue.tuyenSinhTienSi); 
                                        setPage(1); 
                                        replaceHistory(1, pageSize, searchKeyword, loaiTuyenSinhValue.tuyenSinhTienSi);
                                    }}
                                    className="font-weight-bold"
                                    style={{ cursor: 'pointer' }}
                                >Biểu mẫu Tiến sĩ</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link
                                    active={activeTab === loaiTuyenSinhValue.tuyenSinhDTLT}
                                    onClick={() => { 
                                        setActiveTab(loaiTuyenSinhValue.tuyenSinhDTLT); 
                                        setPage(1); 
                                        replaceHistory(1, pageSize, searchKeyword, loaiTuyenSinhValue.tuyenSinhDTLT);
                                    }}
                                    className="font-weight-bold"
                                    style={{ cursor: 'pointer' }}
                                >Biểu mẫu đào tạo liên tục</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link
                                    active={activeTab === loaiTuyenSinhValue.tuyenSinhDTTH}
                                    onClick={() => { 
                                        setActiveTab(loaiTuyenSinhValue.tuyenSinhDTTH); 
                                        setPage(1); 
                                        replaceHistory(1, pageSize, searchKeyword, loaiTuyenSinhValue.tuyenSinhDTTH);
                                    }}
                                    className="font-weight-bold"
                                    style={{ cursor: 'pointer' }}
                                >Biểu mẫu đào tạo thực hành</Nav.Link>
                            </Nav.Item>
                        </Nav>
                        <div className="d-flex align-items-center">
                            {canAdd && (
                                <Button variant="success" className="ml-3" onClick={handleAdd}>
                                    Thêm mới
                                </Button>
                            )}
                        </div>
                    </div>
                </Card.Header>
                <Card.Body className="d-flex flex-column p-0">
                    <Card className="card-sticky-layout border-0 shadow-none flex-grow-1">
                        <Card.Body className="d-flex flex-column">
                            <div className="d-flex justify-content-between mb-3">
                        <form onSubmit={handleSearch} className="d-flex w-50">
                            <Form.Control
                                type="text"
                                placeholder="Tìm kiếm theo tên biểu mẫu..."
                                value={keyword}
                                onChange={(e) => {
                                    setKeyword(e.target.value);
                                    setSearchKeyword(e.target.value);
                                    setPage(1);
                                }}
                                className="mr-2"
                            />
                            <Button type="submit" variant="primary">
                                <i className="fas fa-search"></i>
                            </Button>
                        </form>
                    </div>

                            {isLoading ? (
                                <div className="text-center p-5 m-auto">
                                    <Spinner animation="border" variant="primary" />
                                </div>
                            ) : (
                                <>
                                    <div className="table-responsive flex-grow-1">
                                        <Table striped bordered hover>
                                            <thead>
                                                <tr>
                                                    <th className="text-center" width="50">STT</th>
                                                    <th>Tên biểu mẫu</th>
                                                    <th>Mô tả</th>
                                                    <th className="text-center">Loại tuyển sinh</th>
                                                    <th className="text-center">Đường dẫn</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {items.length > 0 ? (
                                                    items.map((item, index) => (
                                                        <tr 
                                                            key={item.id}
                                                            onClick={() => setSelectedRow(item)}
                                                            style={{ cursor: 'pointer' }}
                                                            className={selectedRow?.id === item.id ? 'table-primary' : ''}
                                                        >
                                                            <td className="text-center">{(page - 1) * pageSize + index + 1}</td>
                                                            <td>{item.tenBieuMau}</td>
                                                            <td dangerouslySetInnerHTML={{ __html: item.moTa }}></td>
                                                            <td className="text-center">{getLabelFromEnum(item.loaiTuyenSinh, objectLoaiTuyenSinh, loaiTuyenSinhValue)}</td>
                                                            <td className="text-center">
                                                                {item.duongDanDrive ? (
                                                                    <a 
                                                                        href={item.duongDanDrive.startsWith('http') ? item.duongDanDrive : `http://${item.duongDanDrive}`} 
                                                                        target="_blank" 
                                                                        rel="noreferrer" 
                                                                        className="text-primary"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        style={{ wordBreak: 'break-all', display: 'inline-block', maxWidth: '300px' }}
                                                                    >
                                                                        {item.duongDanDrive}
                                                                    </a>
                                                                ) : (
                                                                    <span className="text-muted">Không có</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="5" className="text-center text-muted">
                                                            Chưa có dữ liệu biểu mẫu
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>

                                    <div className="d-flex justify-content-between align-items-center mt-3 p-2">
                                        <div>
                                            <Form.Control as="select" value={pageSize} onChange={onChangeShowNumber} style={{ width: '100px' }}>
                                                {objectShowNumber.map((opt) => (
                                                    <option key={opt.value} value={opt.value}>{opt.text}</option>
                                                ))}
                                            </Form.Control>
                                        </div>
                                        <div>
                                            <Pagination
                                                itemClass="page-item"
                                                linkClass="page-link"
                                                innerClass="pagination pagination-sm no-margin"
                                                activePage={page}
                                                itemsCountPerPage={pageSize}
                                                totalItemsCount={totalRecords}
                                                pageRangeDisplayed={5}
                                                onChange={changePage}
                                            />
                                        </div>
                                    </div>
                                    
                                    <ConfirmModal
                                        isOpen={openConfirmDeleteModal}
                                        confirmType="delete"
                                        goBackAfterDelete={false}
                                        handleAccept={acceptDelete}
                                        handleCancel={cancelDelete}
                                        content={confirmDelMessage}
                                    />
                                </>
                            )}
                        </Card.Body>
                        {!isLoading && (canEdit || canDelete) && (
                            <Card.Footer className="bg-white card-footer-sticky">
                                <div className="d-flex justify-content-end pr-4">
                                    {canEdit && (
                                        <Button 
                                            variant="info" 
                                            size="sm" 
                                            className="mr-2" 
                                            onClick={() => handleEdit(selectedRow)}
                                            disabled={!selectedRow}
                                        >
                                            <i className="fas fa-edit mr-1"></i> Sửa
                                        </Button>
                                    )}
                                    {canDelete && (
                                        <Button 
                                            variant="danger" 
                                            size="sm" 
                                            onClick={() => deleteAction(selectedRow.id, selectedRow.tenBieuMau)}
                                            disabled={!selectedRow}
                                        >
                                            <i className="fas fa-trash mr-1"></i> Xóa
                                        </Button>
                                    )}
                                </div>
                            </Card.Footer>
                        )}
                    </Card>
                </Card.Body>
            </Card>

            <BieuMauTuyenSinhAdd
                isOpen={showModalAdd}
                onClose={() => setShowModalAdd(false)}
                activeTab={activeTab}
                refreshList={refetch}
            />
            {dataEdit && (
                <BieuMauTuyenSinhEdit
                    isOpen={showModalEdit}
                    onClose={() => { setShowModalEdit(false); setDataEdit(null); }}
                    dataEdit={dataEdit}
                    activeTab={activeTab}
                    refreshList={refetch}
                />
            )}
        </div>
    );
};

export default BieuMauTuyenSinh;
